import { isClearingBankPayment, isStaffCancelledPayment } from '@/lib/payments/savedPaymentMethods';
import { useApiQuery } from '../useApiQuery';
import { invoiceKeys } from '@/lib/queryKeys';
import { apiGet, operationsClient } from '@/lib/api/client';
import { cardServiceFee, collectedTotal } from '@/lib/payments/collectedAmount';
import type { Payment } from '@/types/invoice';

interface PaymentsResponse {
  data: Record<string, unknown>[];
  total: number;
}

export function normalizePayments(p: Record<string, unknown>): Payment[] {
  const status = String(p.status ?? '').toUpperCase();
  const gatewayMetadata =
    p.gatewayMetadata && typeof p.gatewayMetadata === 'object' && !Array.isArray(p.gatewayMetadata)
      ? (p.gatewayMetadata as Record<string, unknown>)
      : p.gateway_metadata &&
          typeof p.gateway_metadata === 'object' &&
          !Array.isArray(p.gateway_metadata)
        ? (p.gateway_metadata as Record<string, unknown>)
        : null;
  const isTipPayment =
    gatewayMetadata?.source === 'public_tip_checkout' ||
    gatewayMetadata?.type === 'tip_only' ||
    gatewayMetadata?.publicTipCheckout === true;
  const inlineTipCents = Number(
    gatewayMetadata?.tipCents ??
      gatewayMetadata?.tipAmountCents ??
      gatewayMetadata?.inlineTipCents ??
      0,
  );
  const baseAmountCents = Number(gatewayMetadata?.baseAmountCents ?? 0);
  const hasInlineTip = Number.isFinite(inlineTipCents) && inlineTipCents > 0;
  const principalAmount = hasInlineTip
    ? (Number.isFinite(baseAmountCents) && baseAmountCents > 0
        ? Math.round(baseAmountCents)
        : Math.max(0, Math.round(Number(p.amount ?? 0) * 100) - Math.round(inlineTipCents))) / 100
    : Number(p.amount ?? 0);
  const refundReason =
    p.refundReason != null
      ? String(p.refundReason)
      : p.refund_reason != null
        ? String(p.refund_reason)
        : undefined;
  const failureReason =
    p.failureReason != null
      ? String(p.failureReason)
      : p.failure_reason != null
        ? String(p.failure_reason)
        : undefined;
  const refundedAt =
    p.refundedAt != null
      ? String(p.refundedAt)
      : p.refunded_at != null
        ? String(p.refunded_at)
        : undefined;
  const disputedAt =
    status === 'DISPUTED' ||
    status === 'DISPUTE_UNDER_REVIEW' ||
    status === 'CHARGEBACK_LOST' ||
    status === 'CHARGEBACK_WON'
      ? p.disputedAt != null
        ? String(p.disputedAt)
        : p.updatedAt != null
          ? String(p.updatedAt)
          : p.updated_at != null
            ? String(p.updated_at)
            : p.processedAt != null
              ? String(p.processedAt)
              : p.createdAt != null
                ? String(p.createdAt)
                : undefined
      : undefined;
  const disputeResolvedAt =
    p.disputeResolvedAt != null
      ? String(p.disputeResolvedAt)
      : p.dispute_resolved_at != null
        ? String(p.dispute_resolved_at)
        : undefined;
  const refundedAmount = Number(p.refundedAmount ?? p.refunded_amount ?? p.amount ?? 0);
  const isDisputeFamily =
    status === 'DISPUTED' ||
    status === 'DISPUTE_UNDER_REVIEW' ||
    status === 'CHARGEBACK_LOST' ||
    status === 'CHARGEBACK_WON';
  // How much of this payment the server says landed on the invoice being
  // viewed. Present only for a payment that reached the invoice through an
  // application — a deposit capture, whose `amount` is the whole deposit and
  // can exceed what this invoice received.
  const rawApplied = p.invoiceAppliedAmount;
  const appliedAmount = rawApplied == null || rawApplied === '' ? null : Number(rawApplied);
  const hasApplied = appliedAmount != null && Number.isFinite(appliedAmount);
  const appliedSource = p.invoiceAppliedSource != null ? String(p.invoiceAppliedSource) : null;
  const isDepositApplication = hasApplied && appliedSource === 'deposit';
  // The fee was charged on the WHOLE capture, so it only describes this row
  // when this invoice received the whole capture. On a split deposit the fee
  // belongs to neither slice on its own, and printing it beside a part would
  // not reconcile with the amount next to it.
  const appliedInFull = hasApplied && Math.abs(appliedAmount - Number(p.amount ?? 0)) < 0.005;

  // A scanned check's reference is the number read off the paper; it is what
  // the office looks for in the notes, as on a hand-recorded check.
  const scannedCheckNumber =
    p.paymentMethod != null &&
    p.flow === 'CHEQUE_SCAN' &&
    typeof p.reference === 'string' &&
    p.reference.trim()
      ? p.reference.trim()
      : null;

  const paymentBase: Payment = {
    id: String(p.id ?? p._id ?? ''),
    paymentNumber: String(p.paymentNumber ?? '') || undefined,
    paymentId: String(p.id ?? p._id ?? ''),
    kind: 'payment',
    amount: hasApplied ? appliedAmount : principalAmount,
    method: String(p.paymentMethod ?? p.method ?? 'other').toLowerCase() as Payment['method'],
    // Only beside a method the caller may see (see `paymentMethodLabel`).
    flow: p.paymentMethod != null && p.flow != null ? String(p.flow) : null,
    date: String(p.processedAt ?? p.date ?? p.createdAt ?? ''),
    // A bank payment staff cancelled before it cleared is stored FAILED; it
    // reads "Cancelled", since the bank did not refuse it.
    status: isStaffCancelledPayment({ status, gatewayMetadata })
      ? 'CANCELLED'
      : status === 'REFUNDED' ||
          status === 'PARTIALLY_REFUNDED' ||
          status === 'DISPUTED' ||
          status === 'DISPUTE_UNDER_REVIEW' ||
          status === 'CHARGEBACK_LOST' ||
          status === 'CHARGEBACK_WON'
        ? 'COMPLETED'
        : status,
    notes:
      (isDepositApplication ? 'Deposit applied' : undefined) ??
      (isTipPayment ? 'Tip payment' : undefined) ??
      (hasInlineTip
        ? `Payment received (+tip ${(Math.round(inlineTipCents) / 100).toFixed(2)})`
        : undefined) ??
      (!isDisputeFamily ? failureReason : undefined) ??
      (scannedCheckNumber ? `Check #${scannedCheckNumber}` : undefined) ??
      (p.notes != null ? String(p.notes) : status === 'COMPLETED' ? 'Payment received' : ''),
    refundedAt,
    refundReason,
    refundedAmount,
    gatewayProvider:
      p.gatewayProvider != null
        ? String(p.gatewayProvider)
        : p.gateway_provider != null
          ? String(p.gateway_provider)
          : null,
    gatewayTxnId:
      p.gatewayTxnId != null
        ? String(p.gatewayTxnId)
        : p.gateway_txn_id != null
          ? String(p.gateway_txn_id)
          : null,
    updatedBy:
      p.updatedBy != null
        ? String(p.updatedBy)
        : p.updated_by != null
          ? String(p.updated_by)
          : null,
    disputedAt,
    // Prisma Decimal — reaches the browser as "3.00", and is absent entirely
    // for a role whose field mask withholds pricing. `cardServiceFee` coerces
    // and floors at 0, so a reversed fee (zeroed after a debit card was
    // detected) correctly shows nothing rather than a fee that came back.
    surchargeAmount: hasApplied && !appliedInFull ? undefined : cardServiceFee(p),
    // From the RAW server amount, not `principalAmount`: the server's `amount`
    // already carries the inline tip, and this entry's `amount` has had it
    // split off into its own row. Deriving the charged total from the principal
    // would silently drop the tip out of "what the customer paid".
    chargedTotal: hasApplied && !appliedInFull ? undefined : collectedTotal(p),
    invoiceAppliedAmount: hasApplied ? appliedAmount : undefined,
    invoiceAppliedSource: appliedSource,
    createdAt: String(p.createdAt ?? ''),
  };

  const entries: Payment[] = [paymentBase];

  if (hasInlineTip) {
    const tipAmount = Math.round(inlineTipCents) / 100;
    entries.push({
      id: `${paymentBase.id}-tip`,
      paymentNumber: paymentBase.paymentNumber,
      paymentId: paymentBase.paymentId,
      kind: 'tip',
      amount: tipAmount,
      method: paymentBase.method,
      date: paymentBase.date,
      status: paymentBase.status,
      notes: 'Tip added',
      createdAt: paymentBase.createdAt,
    });
  }

  if (refundedAt && (status === 'REFUNDED' || status === 'PARTIALLY_REFUNDED')) {
    entries.push({
      id: `${paymentBase.id}-refund`,
      paymentNumber: paymentBase.paymentNumber,
      paymentId: paymentBase.paymentId,
      kind: 'refund',
      amount: refundedAmount,
      method: paymentBase.method,
      date: refundedAt,
      status: 'REFUNDED',
      notes: refundReason ?? 'Payment refunded',
      refundedAt,
      refundReason,
      refundedAmount,
      createdAt: paymentBase.createdAt,
    });
  }

  if (
    disputedAt &&
    (status === 'DISPUTED' ||
      status === 'DISPUTE_UNDER_REVIEW' ||
      status === 'CHARGEBACK_LOST' ||
      status === 'CHARGEBACK_WON')
  ) {
    entries.push({
      id: `${paymentBase.id}-dispute`,
      paymentNumber: paymentBase.paymentNumber,
      paymentId: paymentBase.paymentId,
      kind: 'dispute',
      amount: paymentBase.amount,
      method: paymentBase.method,
      date: disputedAt,
      status: status === 'DISPUTE_UNDER_REVIEW' ? 'DISPUTE_UNDER_REVIEW' : 'DISPUTED',
      notes: status === 'DISPUTE_UNDER_REVIEW' ? 'Dispute under review' : 'Payment disputed',
      disputedAt,
      createdAt: paymentBase.createdAt,
    });
  }

  if (disputeResolvedAt && (status === 'CHARGEBACK_LOST' || status === 'CHARGEBACK_WON')) {
    entries.push({
      id: `${paymentBase.id}-dispute-outcome`,
      paymentNumber: paymentBase.paymentNumber,
      paymentId: paymentBase.paymentId,
      kind: 'dispute',
      amount: paymentBase.amount,
      method: paymentBase.method,
      date: disputeResolvedAt,
      status,
      notes: status === 'CHARGEBACK_WON' ? 'Chargeback won' : 'Chargeback lost',
      disputedAt: disputeResolvedAt,
      createdAt: paymentBase.createdAt,
    });
  }

  return entries;
}

export function invoicePaymentsQueryOptions(invoiceId: string) {
  return {
    queryKey: [...invoiceKeys.detail(invoiceId), 'payments'],
    queryFn: async (): Promise<Payment[]> => {
      const raw = await apiGet<PaymentsResponse>(
        operationsClient,
        `/invoices/${invoiceId}/payments`,
      );
      return (raw.data ?? [])
        .flatMap(normalizePayments)
        .sort(
          (a, b) =>
            new Date(b.date || b.createdAt).getTime() - new Date(a.date || a.createdAt).getTime(),
        );
    },
  };
}

export function useInvoicePayments(invoiceId: string, options?: { enabled?: boolean }) {
  return useApiQuery<Payment[]>({
    ...invoicePaymentsQueryOptions(invoiceId),
    enabled: (options?.enabled ?? true) && Boolean(invoiceId) && invoiceId !== 'new',
    refetchInterval: (query) =>
      query.state.data?.some((payment) =>
        isClearingBankPayment({ ...payment, paymentMethod: payment.method }),
      )
        ? 5_000
        : false,
    showErrorToast: false,
  });
}
